import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useAuth, useFeedback } from "@taunggyi-plus/auth";
import {
  Card,
  ManagementListToolbar,
  PaginatedList,
  ServerPagination,
  RecordFormDialog,
  StatusPill,
} from "@taunggyi-plus/design-system";
type Driver = {
  id: string;
  user_id: string | null;
  display_name: string;
  phone: string;
  vehicle_type: string;
  vehicle_number: string;
  vehicle_color: string;
  photo_data_url: string | null;
  is_available: boolean;
  is_verified: boolean;
  email: string | null;
  username: string | null;
  area: string;
};
export function AdminTaxiDrivers({
  createOnly = false,
  onCreated,
  onClose,
}: {
  createOnly?: boolean;
  onCreated?: () => void;
  onClose?: () => void;
}) {
  const auth = useAuth();
  const [page, setPage] = useState(0),
    [total, setTotal] = useState(0),
    [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>([]),
    [editing, setEditing] = useState<Driver | null>(null),
    [open, setOpen] = useState(createOnly),
    [query, setQuery] = useState(""),
    [status, setStatus] = useState("all"),
    [busy, setBusy] = useState(false),
    [loaded, setLoaded] = useState(false),
    [message, setMessage] = useFeedback();
  const [photo, setPhoto] = useState<string | null>(null),
    [photoBusy, setPhotoBusy] = useState(false),
    [photoError, setPhotoError] = useState("");
  const photoSelection = useRef(0),
    photoInput = useRef<HTMLInputElement>(null);
  const load = useCallback(async () => {
    if (!auth.client || createOnly) return;
    setLoading(true);
    const r = await auth.client.rpc("admin_driver_page", {
      page,
      query,
      verification: status,
      target: new URLSearchParams(window.location.search).get("record"),
    });
    setLoading(false);
    if (r.error) {
      setMessage(r.error.message);
      return;
    }
    const result = r.data as { items: Driver[]; total: number };
    setDrivers((old) =>
      page === 0
        ? result.items
        : [...old.filter((d) => !result.items.some((n) => n.id === d.id)), ...result.items],
    );
    setTotal(result.total);
    setLoaded(true);
  }, [auth.client, setMessage, page, query, status, createOnly]);
  useEffect(() => {
    const timer = setTimeout(() => void load(), 0);
    return () => clearTimeout(timer);
  }, [load]);
  async function selectPhoto(file: File | undefined) {
    if (!file) return;
    const selection = ++photoSelection.current;
    setPhotoError("");
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      file.size > 5 * 1024 * 1024
    ) {
      setPhotoError("Choose a JPEG, PNG, or WebP photo up to 5 MB.");
      return;
    }
    setPhotoBusy(true);
    const url = URL.createObjectURL(file);
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      const scale = Math.min(1, 640 / Math.max(image.naturalWidth, image.naturalHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Could not prepare this photo.");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const prepared = canvas.toDataURL("image/jpeg", 0.8);
      if (prepared.length > 524288) throw new Error("Please choose a smaller photo.");
      if (selection === photoSelection.current) setPhoto(prepared);
    } catch (error) {
      if (selection === photoSelection.current)
        setPhotoError(error instanceof Error ? error.message : "Could not load the photo.");
    } finally {
      URL.revokeObjectURL(url);
      if (selection === photoSelection.current) setPhotoBusy(false);
    }
  }
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!auth.client || busy || photoBusy) return;
    const f = new FormData(event.currentTarget);
    setBusy(true);
    setMessage("");
    try {
      const r = await auth.client.rpc("admin_save_driver_account", {
        target_id: editing?.id ?? null,
        driver_name: f.get("name"),
        driver_phone: f.get("phone"),
        vehicle: f.get("vehicle"),
        vehicle_no: f.get("number"),
        color: f.get("color"),
        area: f.get("area") ?? editing?.area ?? "",
        photo,
        available: f.get("available") === "on",
        verified: f.get("verified") === "on",
        email: f.get("email") ?? "",
        username: f.get("username") ?? "",
        initial_password: f.get("password") ?? "",
      });
      if (r.error) throw new Error(r.error.message);
      setOpen(false);
      await load();
      setMessage("Driver account saved.");
      onCreated?.();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Could not save driver.");
    } finally {
      setBusy(false);
    }
  }
  const openEditor = (d: Driver | null) => {
    setEditing(d);
    setPhoto(d?.photo_data_url ?? null);
    setPhotoError("");
    setMessage("");
    setOpen(true);
  };
  return (
    <section className="filtered-list-stack">
      {!createOnly && (
        <>
          <div className="route-heading">
            <h1>Taxi drivers</h1>
          </div>
          {message && <p role="status">{message}</p>}
          {!loaded && <p>Loading drivers…</p>}
          <ManagementListToolbar
            actions={
              <button
                className="toolbar-icon-button"
                aria-label="Add taxi driver"
                onClick={() => openEditor(null)}
              >
                +
              </button>
            }
            query={query}
            searchLabel="Search drivers"
            onQueryChange={(q) => {
              setPage(0);
              setQuery(q);
            }}
            resultCount={total}
            onClear={() => {
              setPage(0);
              setQuery("");
              setStatus("all");
            }}
            filters={[
              {
                label: "Verification",
                value: status,
                onChange: (value) => {
                  setPage(0);
                  setStatus(value);
                },
                options: [
                  { value: "all", label: "All drivers" },
                  { value: "verified", label: "Verified" },
                  { value: "unverified", label: "Not verified" },
                ],
              },
            ]}
          />
          <PaginatedList
            pagination="external"
            label="Taxi driver pages"
            items={drivers}
            resetKey={`${query}:${status}`}
            renderItem={(d) => (
              <Card key={d.id}>
                <h3>{d.display_name}</h3>
                <StatusPill tone={d.is_verified ? "success" : "warning"}>
                  {d.is_verified ? "Verified" : "Not verified"}
                </StatusPill>
                <p>
                  {d.vehicle_color || "Color not provided"} · {d.vehicle_number}
                </p>
                {d.photo_data_url && (
                  <img
                    src={d.photo_data_url}
                    alt={`${d.display_name} portrait`}
                    style={{ width: 160, borderRadius: 12 }}
                  />
                )}
                <p>
                  {d.email ?? "No sign-in account yet"} {d.username && `· ${d.username}`}
                </p>
                <p>Private contact: {d.phone}</p>
                <button className="button button--secondary" onClick={() => openEditor(d)}>
                  {d.user_id ? "Edit driver" : "Create driver account"}
                </button>
              </Card>
            )}
          />
          {total > 10 && !new URLSearchParams(window.location.search).has("record") && (
            <ServerPagination page={page} total={total} loading={loading} onPage={setPage} />
          )}
        </>
      )}
      {open && (
        <RecordFormDialog
          open
          hideTrigger
          className="driver-account-dialog"
          title={editing ? "Edit driver" : "Create driver account"}
          onClose={() => {
            setOpen(false);
            photoSelection.current++;
            onClose?.();
          }}
        >
          <form className="form-grid driver-account-form" onSubmit={save}>
            <fieldset disabled={busy} className="driver-account-form__section">
              <legend>Driver & vehicle</legend>
              <label className="field">
                Driver photo
                <input
                  ref={photoInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={photoBusy || busy}
                  onChange={(e) => void selectPhoto(e.target.files?.[0])}
                />
              </label>
              {photo && (
                <>
                  <img
                    src={photo}
                    alt="Driver photo preview"
                    style={{ width: 120, borderRadius: 12 }}
                  />
                  <button
                    className="button button--secondary"
                    type="button"
                    onClick={() => setPhoto(null)}
                  >
                    Remove photo
                  </button>
                </>
              )}
              {photoError && <p role="alert">{photoError}</p>}
              <label className="field">
                Driver name
                <input name="name" required maxLength={160} defaultValue={editing?.display_name} />
              </label>
              <label className="field">
                Private phone
                <input
                  name="phone"
                  type="tel"
                  required
                  minLength={5}
                  maxLength={40}
                  defaultValue={editing?.phone}
                />
                <small>Not displayed to customers.</small>
              </label>
              <label className="field">
                Vehicle model
                <input
                  name="vehicle"
                  required
                  maxLength={100}
                  defaultValue={editing?.vehicle_type}
                />
              </label>
              <label className="field">
                Car color
                <input name="color" required maxLength={80} defaultValue={editing?.vehicle_color} />
              </label>
              <label className="field">
                Vehicle number
                <input
                  name="number"
                  required
                  maxLength={100}
                  defaultValue={editing?.vehicle_number}
                />
              </label>
              {!editing && (
                <label className="field">
                  Service area
                  <input name="area" required maxLength={300} />
                </label>
              )}
            </fieldset>
            <fieldset disabled={busy} className="driver-account-form__section">
              <legend>Sign-in details</legend>
              {!editing?.user_id && (
                <>
                  <label className="field">
                    Login email
                    <input name="email" type="email" required autoComplete="off" />
                  </label>
                  <label className="field">
                    Username
                    <input
                      name="username"
                      pattern="[a-zA-Z0-9_]{3,30}"
                      title="3–30 letters, numbers, or underscores"
                      required
                      autoComplete="off"
                    />
                  </label>
                </>
              )}
              <label className="field">
                {editing?.user_id ? "New password (optional)" : "Initial password"}
                <input
                  name="password"
                  type="password"
                  required={!editing?.user_id}
                  minLength={12}
                  maxLength={72}
                  autoComplete="new-password"
                />
                <small>Use at least 12 characters.</small>
              </label>
            </fieldset>
            <fieldset
              disabled={busy}
              className="driver-account-form__section driver-account-form__section--status"
            >
              <legend>Driver status</legend>
              <label className="driver-account-form__check">
                <input
                  type="checkbox"
                  name="verified"
                  defaultChecked={editing?.is_verified ?? true}
                />{" "}
                Verified driver
              </label>
              <label className="driver-account-form__check">
                <input
                  type="checkbox"
                  name="available"
                  defaultChecked={editing?.is_available ?? true}
                />{" "}
                Available for rides and calls
              </label>
            </fieldset>
            {!editing && (
              <small>After creating the driver, manage their subscription in Admin Partners.</small>
            )}
            {message && <p role="alert">{message}</p>}
            <footer className="driver-account-form__actions">
              <button
                type="button"
                className="button button--secondary"
                disabled={busy}
                onClick={() => {
                  setOpen(false);
                  photoSelection.current++;
                  onClose?.();
                }}
              >
                Cancel
              </button>
              <button className="button" disabled={busy || photoBusy}>
                {busy ? "Saving…" : "Save driver account"}
              </button>
            </footer>
          </form>
        </RecordFormDialog>
      )}
    </section>
  );
}
